// src/components/DinnerBooking.js
// Platinum only — book a private dinner. Member pays the bill only.
import { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { createDinnerBooking, getUserBookings } from '../lib/supabase'
import './DinnerBooking.css'

export default function DinnerBooking() {
  const { session } = useAuth()
  const userId = session?.user?.id

  const [form, setForm] = useState({ date: '', city: '', note: '' })
  const [bookings, setBookings] = useState([])
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!userId) return
    getUserBookings(userId).then(({ data }) => { if (data) setBookings(data) })
  }, [userId])

  function handleChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    if (!form.date || !form.city) { setError('Please fill in date and city.'); return }
    setSubmitting(true)
    const { data, error: err } = await createDinnerBooking({
      userId,
      date: form.date,
      city: form.city,
      note: form.note
    })
    setSubmitting(false)
    if (err) { setError(err.message); return }
    if (data) setBookings(prev => [data, ...prev])
    setForm({ date: '', city: '', note: '' })
    setSuccess(true)
    setTimeout(() => setSuccess(false), 5000)
  }

  const statusLabel = { pending: 'Pending Review', approved: 'Confirmed ✓', declined: 'Declined' }
  const statusClass = { pending: 'booking-status--pending', approved: 'booking-status--approved', declined: 'booking-status--declined' }

  return (
    <div className="dinner-booking">
      <div className="dinner-booking__header">
        <div className="dinner-booking__header-top">
          <span className="dinner-booking__eyebrow">Platinum Exclusive</span>
          <span className="badge badge--platinum">Dinner Booking</span>
        </div>
        <h2 className="dinner-booking__title">Book a Private Dinner</h2>
        <p className="dinner-booking__desc">
          An intimate one-on-one dinner — just us. You handle the restaurant bill.
          I take care of everything else. Slots are personal and very limited.
        </p>
      </div>

      <form className="dinner-booking__form" onSubmit={handleSubmit}>
        <div className="dinner-booking__form-grid">
          <div className="field">
            <label htmlFor="date">Preferred Date</label>
            <input
              id="date" name="date" type="date"
              value={form.date} onChange={handleChange}
              min={new Date().toISOString().split('T')[0]}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="city">City / Location</label>
            <input
              id="city" name="city" type="text"
              placeholder="e.g. Los Angeles, CA"
              value={form.city} onChange={handleChange} required
            />
          </div>
        </div>
        <div className="field">
          <label htmlFor="note">Special Note (optional)</label>
          <textarea
            id="note" name="note"
            placeholder="Any preferences, special occasions, or requests…"
            value={form.note} onChange={handleChange}
          />
        </div>

        {error && <div className="dinner-booking__error">{error}</div>}
        {success && (
          <div className="dinner-booking__success">
            Request sent 🌹 Sky Bri will reach out within 48 hours.
          </div>
        )}

        <button className="btn btn--plat" type="submit" disabled={submitting}>
          {submitting ? 'Sending Request...' : 'Request Dinner Date'}
        </button>
      </form>

      {/* PAST BOOKINGS */}
      {bookings.length > 0 && (
        <div className="dinner-booking__history">
          <div className="dinner-booking__history-label">Your Requests</div>
          {bookings.map(b => (
            <div key={b.id} className="booking-row">
              <div className="booking-row__info">
                <span className="booking-row__date">
                  {new Date(b.preferred_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </span>
                <span className="booking-row__city">{b.preferred_city}</span>
                {b.special_note && <span className="booking-row__note">"{b.special_note}"</span>}
              </div>
              <span className={`booking-status ${statusClass[b.status]}`}>
                {statusLabel[b.status]}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
